import CheckboxField from "components/controls/CheckboxField";
import DatePickerField from "components/controls/DatePickerField";
import { FilteringCombobox } from "components/controls/FilteringCombobox";
import InputField from "components/controls/InputField";
import { TextareaField } from "components/controls/TextareaField";
import { PackageValidationFields } from "components/PackageValidationFields/PackageValidationFields";
import { Grid } from "components/styled";
import { useFormikContext } from "formik";
import { helpAnchor } from "help/anchor";
import { useAddEnrollmentPackageOptions } from "hooks/useAddEnrollmentPackageOptions";
import {
  IBenefitPackageView,
  IEmployeeEnrollment,
  IEmployeeView,
} from "library/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PackageEditableFields } from "../../../EmployeeActions/PackageEditableFields";
import { EnrollmentConsents } from "./EnrollmentConsents";
import { set } from "date-fns";

type Props = {
  isAddMode?: boolean;
  benefitPackageTag?: number;
  enrollment?: IEmployeeEnrollment;

  withSelectedBenefit?: boolean;
  packageData?: IBenefitPackageView;
  packageUser?: IEmployeeView;
  isAutoEnrollment?: boolean;
  consentsNeeded?: boolean;
};

export function EnrollmentFormFields({
  packageData,
  packageUser,
  enrollment,
  isAddMode = false,
  isAutoEnrollment,
  withSelectedBenefit,
  consentsNeeded,
}: Props) {
  const { t } = useTranslation();
  const employeeDateOfEmployment = packageUser?.dateOfEmployment;

  const { values, setFieldValue, errors } = useFormikContext<FormData>();

  const { options: packagesOptions } = useAddEnrollmentPackageOptions({
    isAddMode,
    employeeLoginEmailAddress: packageUser?.employeeLoginEmailAddress || "",
    benefitPackageTag: withSelectedBenefit
      ? packageData?.benefitPackageTag
      : undefined,
    eligibilities: packageUser?.enrollments || [],
    enrollments: packageUser?.enrollments || [],
  });

  const packageOptionsAsSelectItem = packagesOptions.map((option) => {
    return {
      ...option,
      text: option.name,
      value: option.benefitPackageTag,
    };
  });

  useEffect(() => {
    if (consentsNeeded) {
      setFieldValue("requiresEnrollmentApproval", true);
      return;
    }
    if (isAutoEnrollment) {
      setFieldValue("requiresEnrollmentApproval", false);
      return;
    }
  }, [isAutoEnrollment]);

  return (
    <>
      <Grid $gridColumns={3}>
        {isAddMode ? (
          <FilteringCombobox
            required
            error={errors["package"]}
            setFieldValue={setFieldValue}
            value={values["package"]}
            label={t("hbh.package.label")}
            options={packageOptionsAsSelectItem}
            name={"package"}
          />
        ) : (
          <InputField
            label={t("hbh.package.label")}
            name=""
            value={packageData?.name || ""}
            disabled
            setFieldValue={() => undefined}
          />
        )}
      </Grid>
      <Grid $gridColumns={3}>
        <DatePickerField
          required
          error={errors["dateOfEligibility"]}
          name="dateOfEligibility"
          value={values["dateOfEligibility"] as Date}
          setFieldValue={setFieldValue}
          label={t("hbh.eligibilityDate.label")}
        />
        <DatePickerField
          required
          error={errors["endDateOfEligibility"]}
          name="endDateOfEligibility"
          value={values["endDateOfEligibility"] as Date}
          setFieldValue={setFieldValue}
          label={t("hbh.endEligibilityDate.label")}
        />
        <DatePickerField
          required
          error={errors["cancellationPossibility"]}
          name="cancellationPossibility"
          value={values["cancellationPossibility"] as Date}
          setFieldValue={setFieldValue}
          label={t("hbh.cancellationPossibility.label")}
        />
        <DatePickerField
          dataHelp={helpAnchor("hr-enrollment-form-enrollment-date")}
          error={errors["dateOfEnrollment"]}
          name="dateOfEnrollment"
          value={values["dateOfEnrollment"] as Date}
          setFieldValue={setFieldValue}
          label={t("hbh.enrollmentDate.label")}
        />

        <DatePickerField
          dataHelp={helpAnchor("hr-enrollment-form-cancellation-date")}
          error={errors["dateOfCancellation"]}
          name="dateOfCancellation"
          value={values["dateOfCancellation"] as Date}
          setFieldValue={setFieldValue}
          label={t("hbh.cancelationDate.label")}
        />
      </Grid>
      <Grid>
        <CheckboxField
          name="isUpgradeable"
          value={values["isUpgradeable"]}
          setFieldValue={setFieldValue}
          label={t("hbh.isUpgradeable.label")}
        />
        <CheckboxField
          disabled={isAutoEnrollment || consentsNeeded}
          name="requiresEnrollmentApproval"
          value={values["requiresEnrollmentApproval"]}
          setFieldValue={setFieldValue}
          label={t("hbh.requiresEnrollmentApproval.label")}
        />
        <CheckboxField
          name="requiresCancellationApproval"
          value={values["requiresCancellationApproval"]}
          setFieldValue={setFieldValue}
          label={t("hbh.requiresCancellationApproval.label")}
        />
      </Grid>

      <Grid $gridColumns={1}>
        <TextareaField
          label={t("hbh.cancelationReason.label")}
          name="reasonForCancellation"
          value={values["reasonForCancellation"]}
          setFieldValue={setFieldValue}
          error={errors["reasonForCancellation"]}
          rows={3}
          resize="vertical"
        />
      </Grid>

      <EnrollmentConsents
        enrollment={enrollment}
        packageName={packageData?.name}
      />

      <Grid>
        <PackageEditableFields />
      </Grid>

      <PackageValidationFields
        gridColumns={3}
        isDependent={packageUser?.isDependent}
        packageData={
          {
            ...packageData,
            dateOfEmployment: employeeDateOfEmployment,
          } as IBenefitPackageView & {
            dateOfEmployment: string;
          }
        }
      />
    </>
  );
}
